import { Box, Grid, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { t } from "i18next";
import { useContext } from "react";
import EasyForm, { FormType } from "../../../../../components/Forms/EasyForm";
import EasyFormColumn from "../../../../../components/Forms/EasyFormColumn";
import { DetailedOrganisationResponse } from "../../../../../models/Organisation";
import HostingsiteBase from "../../../../../models/Hostingsite";
import { PaginationQueryPackage } from "../../../../../models/API/QueryParams/PaginationQueryPackage";
import { NotCorrectRights } from "../../../../Permission/NotCorrectRights";
import { PermissionEnum } from "../../../../Permission/PermissionEnum";
import { AbilityContext } from "../../../../UserApi/logic/Can";
import { getOrganisationCategoriesColumn, getOrganisationInfoColumn1, getOrganisationInfoColumn2 } from "../../../organisationInfoColumns";
import { getOrganisationValidationScheme } from "../../../organisationSchemeValidation";
import { useGetOrganisationByIdQuery, useUpdateOrganisationMutation } from "../../../redux/organisationApiSlice";
import OrganisationContactsTab from "./ContactsTab";
import { UserContext } from "../../../../UserApi/logic/FetchUser";
import Loading from "../../../../../components/loading";
import {useGetHostingsitesByOrganisationIdQuery} from "../../../../HostingSiteApi/hostingsitesApiSlice";
import {useGetCPEsByHostingSiteIdQuery} from "../../../../HostingSiteApi/cpeApiSlice";
import {failoverToString, setupTypeToString} from "../../../../../models/CPE";

export default function InfoTab(props: { organisationId?: string }) {
    const ability = useContext(AbilityContext)
    const userContext = useContext(UserContext);
    if (!props.organisationId)
        return <Loading />
    const canReadList = ability.can(PermissionEnum.ORGANISATION_READ, { uuid: props.organisationId })
    if (!canReadList)
        return <NotCorrectRights />

    const isSdnReadOrAdmin = userContext?.allowEmptyOrganisation();
    const categoriesColumn = isSdnReadOrAdmin ? getOrganisationCategoriesColumn() : [];
    return (
        <>
            <Typography variant="h5">{t("Organisation information")}</Typography>
            <Grid container direction={"row"} columnSpacing={9}>
                <Grid item xs={5}>
                    <EasyForm
                        isAllowedToEdit={(organisation) => ability.can(PermissionEnum.ORGANISATION_WRITE, organisation)}
                        arrayPath=""
                        SignupSchema={getOrganisationValidationScheme}
                        saveMutation={useUpdateOrganisationMutation}
                        getByIdAction={useGetOrganisationByIdQuery}
                        columns={[
                            new EasyFormColumn("", "", getOrganisationInfoColumn1().concat(categoriesColumn).concat(getOrganisationInfoColumn2())),
                        ]}
                        formType={FormType.INFORMATION}
                        idToFetch={props.organisationId}
                        getFieldRows={() => []}
                        newButtonTooltip="Add new info"
                        createNew={function (): DetailedOrganisationResponse {
                            throw new Error("Function not implemented.");
                        }}
                    />
                    <HostingSiteOverview organisationId={props.organisationId} />
                </Grid>
                <Grid item xs={7}>
                    <Box paddingTop={"1rem"}>
                        <OrganisationContactsTab organisationId={props.organisationId} />
                    </Box>
                </Grid>
            </Grid>
        </>
    )
}

function HostingSiteOverview(props: { organisationId: string }) {
    const { data: hostingSites = [] } = useGetHostingsitesByOrganisationIdQuery({
        uuid: props.organisationId,
        pagination: new PaginationQueryPackage(),
    });

    if (!hostingSites.length)
        return <></>

    return (
        <Box mt={5}>
            <Typography variant="h6" gutterBottom>
                {t("SDN connection information")}
            </Typography>
            <Table size="small">
                {hostingSites.map((site) => (
                    <HostingSiteRow key={site.uuid} site={site} />
                ))}
            </Table>
        </Box>
    )
}

function HostingSiteRow(props: { site: HostingsiteBase }) {
    const { site } = props;
    const { data: cpes = [] } = useGetCPEsByHostingSiteIdQuery({
        uuid: site.uuid!,
        pagination: new PaginationQueryPackage(),
    });

    return (
        <TableBody>
            <TableRow>
                <TableCell colSpan={4} sx={{ borderBottom: "none", pt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{site.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("address")}: {site.address || "—"}
                    </Typography>
                </TableCell>
            </TableRow>
            {cpes.length > 0 && (
                <TableRow>
                    <TableCell sx={{ color: "text.secondary" }}>{t("SDN v4 ID")}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{t("Vlan names")}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{t("Setup")}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{t("Failover")}</TableCell>
                </TableRow>
            )}
            {cpes.map((cpe) => (
                <TableRow key={cpe.uuid}>
                    <TableCell>{cpe.sdn_v4_id || "—"}</TableCell>
                    <TableCell>{cpe.vlan_names?.join(", ") || "—"}</TableCell>
                    <TableCell>{cpe.setup_type ? setupTypeToString(cpe.setup_type) : "—"}</TableCell>
                    <TableCell>{cpe.failover ? t(failoverToString(cpe.failover)) : "—"}</TableCell>   
                </TableRow>
            ))}
        </TableBody>
    );
}